import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextProps {
  session: Session | null;
  user: User | null;
  signUp: (data: { 
    email: string; 
    password: string; 
    options?: { 
      data?: Record<string, any> 
    } 
  }) => Promise<any>;
  signIn: (data: { email: string; password: string }) => Promise<any>;
  signOut: () => Promise<any>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Set up the listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Update last_login when user signs in (non-blocking)
        if (event === 'SIGNED_IN' && session?.user) {
          // Use setTimeout to make this non-blocking
          setTimeout(async () => {
            try {
              // Check if profile exists and update last_login
              const { data: existingProfile, error: profileFetchError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', session.user.id)
                .single();
              
              if (profileFetchError && profileFetchError.code !== 'PGRST116') {
                console.error('Error fetching profile:', profileFetchError);
                return;
              }
              
              if (existingProfile) {
                // Update last_login for existing profile
                const { error: updateError } = await supabase
                  .from('profiles')
                  .update({ last_login: new Date().toISOString() })
                  .eq('id', session.user.id);
                
                if (updateError) {
                  console.error('Error updating last_login:', updateError);
                }
              }
            } catch (error) {
              console.error('Error updating last_login:', error);
            }
          }, 0);
        }
      }
    );

    // Cleanup on unmount
    return () => subscription.unsubscribe();
  }, []);

  const value = {
    session,
    user,
    loading,
    signUp: async (data: { 
      email: string; 
      password: string; 
      options?: { 
        data?: Record<string, any> 
      } 
    }) => {
      const { data: responseData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: data.options
      });
      
      if (error) {
        throw error;
      }
      return { data: responseData, error };
    },
    signIn: (data: { email: string; password: string }) => supabase.auth.signInWithPassword(data),
    signOut: () => supabase.auth.signOut(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextProps {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 